// src/Pages/CouponAssignment/OfferCodesTab.jsx

import React, { useEffect, useState } from "react";
import { Flex, Button, Tag, Space, Tooltip, Alert, Select, Typography, Modal } from "antd";
import { UploadOutlined, ReloadOutlined, RollbackOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import { FilterContainer } from "../../Styles/filterStyle";
import { getOfferCodePool, getOfferCodes, releaseOfferCode } from "../../Services/coupon";
import { formatDate } from "../../utils/date";
import UploadOfferCodesModal from "./UploadOfferCodesModal";

const { Text } = Typography;

// Same colours as the assignment table's statusTag(), so a code reads the same
// here as the assignment that consumed it.
const codeStatusTag = (status) => {
    const color =
        status === "redeemed"
            ? "green"
            : status === "expired"
            ? "red"
            : status === "assigned"
            ? "blue"
            : "default";
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "-";
    return <Tag color={color}>{label}</Tag>;
};

const OfferCodesTab = () => {
    const [isUploadModal, setIsUploadModal] = useState(false);
    // The pool row an upload was launched from - pre-fills the coupon, plan and
    // reference name so a follow-up batch cannot drift onto a new name.
    const [presetPool, setPresetPool] = useState(null);

    // Drill-down filters. Set by clicking a count in the pool table, which is
    // the path that answers "who holds the assigned ones".
    const [couponFilter, setCouponFilter] = useState(null);
    const [planFilter, setPlanFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const skip = (currentPage - 1) * pageSize;

    const {
        data: poolData,
        isLoading: poolLoading,
        refetch: refetchPool,
    } = useQuery({
        queryKey: ["offer-code-pool"],
        queryFn: () => getOfferCodePool(),
        refetchOnWindowFocus: false,
        retry: false,
    });
    const pools = poolData?.pools || [];

    const codeQuery = {
        couponId: couponFilter,
        planId: planFilter,
        status: statusFilter,
        skip,
        limit: pageSize,
    };

    const {
        data: codeData,
        isLoading: codesLoading,
        refetch: refetchCodes,
    } = useQuery({
        queryKey: ["offer-codes", codeQuery],
        queryFn: () => getOfferCodes(codeQuery),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [couponFilter, planFilter, statusFilter]);

    // Clicking a count drills into exactly that slice of that pool.
    const drillInto = (pool, status) => {
        setCouponFilter(pool.coupon_id);
        setPlanFilter(pool.plan_id);
        setStatusFilter(status);
    };

    const countCell = (value, pool, status, danger = false) => (
        <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto", color: danger ? "#cf1322" : undefined }}
            onClick={() => drillInto(pool, status)}
        >
            <Text strong={danger} style={danger ? { color: "#cf1322" } : undefined}>
                {value ?? 0}
            </Text>
        </Button>
    );

    const handleRelease = (row) => {
        Modal.confirm({
            title: "Return this code to the pool?",
            content: `${row.code} will become available again${
                row.assigned_user_email ? ` and will no longer belong to ${row.assigned_user_email}` : ""
            }. The code is still unused at Apple, so it can be handed to someone else.`,
            okText: "Release",
            onOk: async () => {
                const res = await releaseOfferCode({ offer_code_id: row.id });
                if (res) {
                    refetchCodes();
                    refetchPool();
                }
            },
        });
    };

    // Pools that will refuse the next few assignments. This warning is the
    // reason the screen exists - the admin has to find out here, not when an
    // assignment fails.
    const lowPools = pools.filter((p) => p.is_low);

    const poolColumns = [
        {
            title: "Coupon",
            dataIndex: "coupon_code",
            key: "coupon_code",
            render: (t) => <Text strong>{t || "-"}</Text>,
        },
        { title: "Plan", dataIndex: "plan_name", key: "plan_name", render: (t) => t || "-" },
        {
            title: "Reference Name",
            dataIndex: "ios_offer_reference_name",
            key: "ios_offer_reference_name",
            // Monospace: it has to be compared character by character against
            // App Store Connect.
            render: (t) => <Text code>{t || "-"}</Text>,
        },
        {
            title: "Available",
            dataIndex: "available",
            key: "available",
            align: "center",
            render: (v, row) => countCell(v, row, "available", row.is_low),
        },
        {
            title: "Assigned",
            dataIndex: "assigned",
            key: "assigned",
            align: "center",
            render: (v, row) => countCell(v, row, "assigned"),
        },
        {
            title: "Redeemed",
            dataIndex: "redeemed",
            key: "redeemed",
            align: "center",
            render: (v, row) => countCell(v, row, "redeemed"),
        },
        {
            title: "Expired",
            dataIndex: "expired",
            key: "expired",
            align: "center",
            render: (v, row) => countCell(v, row, "expired"),
        },
        { title: "Total", dataIndex: "total", key: "total", align: "center", render: (v) => v ?? 0 },
        {
            title: "Expires",
            dataIndex: "earliest_expiry",
            key: "earliest_expiry",
            render: (t) => (t ? formatDate(t) : "-"),
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => (
                <Tooltip title="Upload a new batch for this offer">
                    <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={() => {
                            setPresetPool(row);
                            setIsUploadModal(true);
                        }}
                    >
                        Upload Codes
                    </Button>
                </Tooltip>
            ),
        },
    ];

    const codeColumns = [
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
            render: (t) => <Text code>{t || "-"}</Text>,
        },
        { title: "Status", dataIndex: "status", key: "status", render: (s) => codeStatusTag(s) },
        { title: "Coupon", dataIndex: "coupon_code", key: "coupon_code", render: (t) => t || "-" },
        { title: "Plan", dataIndex: "plan_name", key: "plan_name", render: (t) => t || "-" },
        {
            title: "Assigned To",
            dataIndex: "assigned_user_email",
            key: "assigned_user_email",
            render: (t) => t || "-",
        },
        {
            title: "Assigned At",
            dataIndex: "assigned_at",
            key: "assigned_at",
            render: (t) => (t ? formatDate(t) : "-"),
        },
        {
            title: "Redeemed At",
            dataIndex: "redeemed_at",
            key: "redeemed_at",
            render: (t) => (t ? formatDate(t) : "-"),
        },
        {
            title: "Expires At",
            dataIndex: "expires_at",
            key: "expires_at",
            render: (t) => (t ? formatDate(t) : "-"),
        },
        { title: "Batch", dataIndex: "batch_id", key: "batch_id", render: (t) => t || "-" },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, row) => {
                // Only an assigned code can come back. A redeemed one is spent
                // at Apple, and an available one is already in the pool.
                if (row.status !== "assigned") return <Text type="secondary">-</Text>;
                return (
                    <Space size="small">
                        <Tooltip title="Return this code to the pool">
                            <Button
                                type="text"
                                shape="circle"
                                icon={<RollbackOutlined />}
                                onClick={() => handleRelease(row)}
                            />
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    const handleCodeTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    return (
        <div>
            <Flex justify="space-between" align="center" style={{ marginBottom: 10 }}>
                <Text type="secondary">
                    iOS coupons hand out one-time Apple codes. Each assignment takes one from the
                    pool - top it up before it runs dry.
                </Text>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            refetchPool();
                            refetchCodes();
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => {
                            setPresetPool(null);
                            setIsUploadModal(true);
                        }}
                    >
                        Upload Codes
                    </Button>
                </Space>
            </Flex>

            {lowPools.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 10 }}
                    message="Offer code pool running low"
                    description={
                        <>
                            {lowPools.map((p) => (
                                <div key={p.offer_plan_id}>
                                    <Text strong>{p.coupon_code}</Text> has {p.available} code
                                    {p.available === 1 ? "" : "s"} left. Upload a new batch before
                                    assigning more.
                                </div>
                            ))}
                        </>
                    }
                />
            )}

            <CustomTable
                columns={poolColumns}
                data={pools}
                loading={poolLoading}
                rowKey={(r) => r.offer_plan_id}
                pagination={false}
                maxHeight="calc(50vh - 120px)"
            />

            <Flex justify="space-between" align="center" style={{ marginTop: 18, marginBottom: 8 }}>
                <h4 style={{ margin: 0 }}>Codes</h4>
            </Flex>

            <FilterContainer style={{ marginBottom: 10 }}>
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="All Coupons"
                    style={{ width: 160 }}
                    value={couponFilter}
                    onChange={(v) => {
                        setCouponFilter(v);
                        // The plan filter only ever comes from a pool row, so it
                        // would otherwise stay pinned to the old coupon's plan.
                        setPlanFilter(null);
                    }}
                    // Sourced from the pools themselves - only coupons that have
                    // an iOS offer can have codes.
                    options={[...new Map(pools.map((p) => [p.coupon_id, p])).values()].map((p) => ({
                        label: p.coupon_code,
                        value: p.coupon_id,
                    }))}
                />
                <Select
                    allowClear
                    placeholder="All Status"
                    style={{ width: 140 }}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                        { label: "Available", value: "available" },
                        { label: "Assigned", value: "assigned" },
                        { label: "Redeemed", value: "redeemed" },
                        { label: "Expired", value: "expired" },
                    ]}
                />
                {(couponFilter || planFilter || statusFilter) && (
                    <Button
                        type="link"
                        onClick={() => {
                            setCouponFilter(null);
                            setPlanFilter(null);
                            setStatusFilter(null);
                        }}
                    >
                        Clear filters
                    </Button>
                )}
            </FilterContainer>

            <CustomTable
                columns={codeColumns}
                data={codeData?.codes || []}
                loading={codesLoading}
                rowKey={(r) => r.id}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: codeData?.total_count || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                    position: ["bottomRight"],
                }}
                onTableChange={handleCodeTableChange}
                maxHeight="calc(100vh - 620px)"
            />

            <UploadOfferCodesModal
                isModalVisible={isUploadModal}
                presetPool={presetPool}
                handleCancel={() => {
                    setIsUploadModal(false);
                    setPresetPool(null);
                    refetchPool();
                    refetchCodes();
                }}
            />
        </div>
    );
};

export default OfferCodesTab;
